import { client, get, post } from '@/api/axios';
import { AgentEndpoints } from '@/api/endpoints/AgentEndpoints';
import { SystemEndpoints } from '@/api/endpoints/SystemEndpoints';
import type { SystemModulesDto } from '@/app/models/SystemModulesDto';
import type {
  AgentChatRequestDto,
  AgentChatResponseDto,
  AgentConversationMessageDto,
  AgentConversationSummaryDto,
  AgentMessageDto,
  AgentMessageRole,
  AgentPageDto,
  AgentProgressEventDto,
} from './_models';

export const getSystemModules = () => get<SystemModulesDto>(SystemEndpoints.modules);

export const sendAgentMessage = async (
  request: AgentChatRequestDto,
  onProgress: (event: AgentProgressEventDto) => void = () => {},
): Promise<AgentChatResponseDto> => {
  const response = await client.post<ReadableStream<Uint8Array>>(AgentEndpoints.chatStream, request, {
    adapter: 'fetch',
    responseType: 'stream',
    // Without application/json the backend cannot serialize its error envelope and returns an empty 200.
    headers: { Accept: 'text/event-stream, application/json' },
    validateStatus: () => true,
  });

  const contentType = String(response.headers['content-type'] ?? '');
  if (response.status < 200 || response.status >= 300 || !contentType.includes('text/event-stream')) {
    const text = await readStream(response.data);
    let message: unknown;
    try {
      message = (JSON.parse(text) as { message?: unknown }).message;
    } catch {
      message = undefined;
    }
    throw new Error(typeof message === 'string' ? message : `HTTP ${response.status}`);
  }

  return readAgentStream(response.data, onProgress);
};

export class AgentStreamDisconnectedError extends Error {
  constructor() {
    super('A conexão com o assistente foi interrompida.');
    this.name = 'AgentStreamDisconnectedError';
  }
}

const readStream = async (stream: ReadableStream<Uint8Array>): Promise<string> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let text = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return text + decoder.decode();
    text += decoder.decode(value, { stream: true });
  }
};

const parseFrame = (frame: string): { event: string; data: string } => {
  let event = 'message';
  const data: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
  }
  return { event, data: data.join('\n') };
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const readAgentStream = async (
  stream: ReadableStream<Uint8Array>,
  onProgress: (event: AgentProgressEventDto) => void,
): Promise<AgentChatResponseDto> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });

    let separator = buffer.match(/\r?\n\r?\n/);
    while (separator?.index !== undefined) {
      const frame = buffer.slice(0, separator.index);
      buffer = buffer.slice(separator.index + separator[0].length);
      const { event, data } = parseFrame(frame);
      if (data) {
        const payload: unknown = JSON.parse(data);
        if (event === 'thinking' && isRecord(payload) && payload.type === 'THINKING') {
          onProgress({ type: 'THINKING' });
        } else if (
          (event === 'tool_call' || event === 'tool_result') &&
          isRecord(payload) &&
          payload.type === (event === 'tool_call' ? 'TOOL_CALL' : 'TOOL_RESULT') &&
          typeof payload.tool_name === 'string'
        ) {
          onProgress({
            type: event === 'tool_call' ? 'TOOL_CALL' : 'TOOL_RESULT',
            tool_name: payload.tool_name,
          });
        } else if (
          event === 'done' &&
          isRecord(payload) &&
          typeof payload.conversation_id === 'string' &&
          typeof payload.answer === 'string' &&
          typeof payload.requires_confirmation === 'boolean'
        ) {
          return payload as AgentChatResponseDto;
        } else if (event === 'error') {
          throw new Error(
            isRecord(payload) && typeof payload.message === 'string' ? payload.message : 'Erro ao processar a mensagem',
          );
        }
      }
      separator = buffer.match(/\r?\n\r?\n/);
    }

    if (done) throw new AgentStreamDisconnectedError();
  }
};

const CONVERSATIONS_PAGE_SIZE = 20;

// Conversation search uses the API-wide POST SearchRequestDto contract.
export const getConversations = (): Promise<AgentPageDto<AgentConversationSummaryDto>> =>
  post<AgentPageDto<AgentConversationSummaryDto>>(AgentEndpoints.conversations, {
    page: 0,
    per_page: CONVERSATIONS_PAGE_SIZE,
  }).then((response) => response.data);

const MESSAGES_PAGE_SIZE = 50;

const getMessagesPage = (conversationId: string, page: number): Promise<AgentPageDto<AgentConversationMessageDto>> =>
  get<AgentPageDto<AgentConversationMessageDto>>(AgentEndpoints.conversationMessages(conversationId), {
    // Query param is camelCase (@RequestParam binding), unlike the JSON bodies.
    params: { page, perPage: MESSAGES_PAGE_SIZE },
  }).then((response) => response.data);

// TOOL_RESULT rows are internal backend payloads and must not reach the thread.
const RENDERED_ROLES = ['USER', 'ASSISTANT', 'TOOL_CALL'];

const toThreadMessages = (
  conversationId: string,
  pageIndex: number,
  page: AgentPageDto<AgentConversationMessageDto>,
): AgentMessageDto[] =>
  page.content
    .filter((message) => RENDERED_ROLES.includes(message.role))
    .map((message, position) => {
      // History rows have no backend id; page coordinates stay stable within the cached fetch.
      const id = `${conversationId}:${pageIndex}:${position}`;
      if (message.role === 'TOOL_CALL') {
        return {
          id,
          role: 'ASSISTANT' as const,
          content: '',
          created_at: message.timestamp,
          tool_activity: message.tool_name ?? 'ferramenta',
        };
      }
      return {
        id,
        role: message.role as AgentMessageRole,
        content: message.content,
        created_at: message.timestamp,
      };
    });

// Messages are paginated in CHRONOLOGICAL order, so the newest ones live on
// the LAST page. Rehydration shows the tail: page 0 alone when it is the
// whole history, otherwise the last two pages (~51-100 most recent messages).
export const getConversationMessages = async (conversationId: string): Promise<AgentMessageDto[]> => {
  const first = await getMessagesPage(conversationId, 0);
  if (first.last) return toThreadMessages(conversationId, 0, first);

  const lastIndex = first.total_pages - 1;
  const tailIndexes = lastIndex === 1 ? [1] : [lastIndex - 1, lastIndex];
  const tail = await Promise.all(tailIndexes.map((index) => getMessagesPage(conversationId, index)));

  const numbered = tailIndexes.map((index, order) => ({ index, page: tail[order] }));
  const pages = lastIndex === 1 ? [{ index: 0, page: first }, ...numbered] : numbered;
  return pages.flatMap(({ index, page }) => toThreadMessages(conversationId, index, page));
};
